import React, { useState, useRef, useEffect } from 'react';
// import { applyTemporaryImage } from '../../../../store/slices/imageSlice';
import { useAppDispatch } from '../../../../store/store';
import {
    selectSelectedImage,
    selectLoadingSave,
    selectLoadingCancel,
    selectImageLayout,
} from '../../../../store/selectors/imageSelectors';
import {
    updateImage,
    updateImageUrl,
    updateLayoutImage,
} from '../../../../shared/api/imageProjectApi';
import { useSelector } from 'react-redux';
import IconSave from '../../../../shared/ui/icons/IconSave';
import IconCancel from '../../../../shared/ui/icons/IconCancel';
import s from './ToolsButtons.module.css';
import { selectActivePrompt } from '../../../../store/selectors/pageSelectors';
import Spinner from '../../../../shared/ui/spinner/Spinner';
import {
    setLoadingSave,
    setLoadingCancel,
    updateSelectedImageSetting,
} from '../../../../store/slices/imageSlice';
import { updateImageLayout } from '../../../../shared/api/imageLayoutApi';
import { selectImageProject } from '../../../../store/selectors/imageSelectors';
import {
    isLayoutChanged,
    isVisualEffectsChanged,
    isVisualSettingsChanged,
    isCanvasColorChanged,
    isTransformationChanged,
} from '../../../../shared/helpers/imageHelpers';

import { getImageSize } from '../../../../shared/helpers/imageHelpers';

const SaveChangesButton = ({ newImageWithEffects }: { newImageWithEffects?: File | null }) => {
    const dispatch = useAppDispatch();
    const selectedImage = useSelector(selectSelectedImage);
    const imageProject = useSelector(selectImageProject);

    const activePrompt = useSelector(selectActivePrompt);
    const imageLayout = useSelector(selectImageLayout);

    const [isCancelPending, setIsCancelPending] = useState(false);
    const loadingSave = useSelector(selectLoadingSave);
    const loadingCancel = useSelector(selectLoadingCancel);
    const prevImageWithEffects = useRef<File | null>(null);

    const changeImageUrl = async () => {
        if (newImageWithEffects && selectedImage?.image) {
            return await dispatch(
                updateImageUrl({
                    imageId: selectedImage.image.id,
                    file: newImageWithEffects,
                }),
            ).unwrap();
        }
    };

    const handleCancelLayout = async () => {
        if (activePrompt === 'layout' && imageProject && imageLayout) {
            dispatch(setLoadingCancel(true));
            console.log('isDefault', isDefault);
            try {
                await dispatch(
                    updateImageLayout({
                        imageProjectId: imageProject.id,
                        layoutId: imageLayout.id,
                        layout: {
                            slug: 'none',
                            order: imageLayout.order,
                            canvas_color: {
                                red: 255,
                                green: 255,
                                blue: 255,
                                gradient: 'none',
                                transparency: 100,
                            },
                            inline_text: imageLayout.inline_text,
                            gap_size: 10,
                            slot_count: 1,
                            transformation: {
                                width: 820,
                                height: 615,
                                rotation: 0,
                                flip: 'none',
                                presets: {
                                    size: 'none',
                                    aspect_ratio: '',
                                },
                            },
                        },
                    }),
                ).unwrap();
            } finally {
                dispatch(setLoadingCancel(false));
            }
        }
    };

    const handleCancelChanges = async () => {
        console.log('activePrompt', activePrompt);
        if (activePrompt === 'layout') {
            await handleCancelLayout();
        } else {
            if (!selectedImage?.image) return;
            dispatch(setLoadingCancel(true));
            try {
                if (activePrompt === 'adjust_visuals' && selectedImage?.image) {
                    const resetCanvasColor = {
                        red: 255,
                        green: 255,
                        blue: 255,
                        gradient: selectedImage.image.canvas_color.gradient || 'none',
                        transparency: 1,
                    };

                    const resetVisualSettings = {
                        brightness: 0,
                        contrast: 0,
                        saturation: 0,
                        shadows: 0,
                        vignette: 0,
                        highlights: 0,
                        vibrance: 0,
                        clarity: 0,
                        hue: 0,
                        blacks: 0,
                        white: 0,
                        grain: 0,
                        red: 1,
                        green: 1,
                        blue: 1,
                    };

                    [
                        {
                            key: 'canvas_color',
                            value: { ...selectedImage.image.canvas_color, ...resetCanvasColor },
                        },
                        {
                            key: 'visual_settings',
                            value: {
                                ...selectedImage.image.visual_settings,
                                ...resetVisualSettings,
                            },
                        },
                    ].forEach((payload) => dispatch(updateSelectedImageSetting(payload as any)));
                }
                if (activePrompt === 'effects') {
                    const resetCanvasColor = {
                        ...selectedImage.image.canvas_color,
                        gradient: 'none',
                    };

                    const resetVisualEffects = {
                        name: 'none',
                        intensity: 100,
                    };

                    [
                        {
                            key: 'canvas_color',
                            value: { ...selectedImage.image.canvas_color, ...resetCanvasColor },
                        },
                        {
                            key: 'visual_effects',
                            value: { ...selectedImage.image.visual_effects, ...resetVisualEffects },
                        },
                    ].forEach((payload) => dispatch(updateSelectedImageSetting(payload as any)));
                }
                if (activePrompt === 'resize') {
                    const originalImage = selectedImage.image.original_url;
                    const { width, height } = await getImageSize(originalImage);

                    const newImage = {
                        width: 820,
                        height: 615,
                        rotation: 0,
                        flip: 'none',
                        presets: { size: 'none', aspect_ratio: '' },
                        crop: { x: 0, y: 0, width: width, height: height, zoom: 1 },
                        x: 0,
                        y: 0,
                    };
                    await dispatch(
                        updateImage({
                            imageId: selectedImage.image.id,
                            transformation: newImage,
                        }),
                    );
                }
                prevImageWithEffects.current = newImageWithEffects || null;
                setIsCancelPending(true);
            } finally {
                dispatch(setLoadingCancel(false));
            }
        }
    };

    useEffect(() => {
        if (
            isCancelPending &&
            newImageWithEffects &&
            prevImageWithEffects.current !== newImageWithEffects
        ) {
            // changeImageUrl();
            setIsCancelPending(false);
        }
    }, [newImageWithEffects, isCancelPending]);

    const isDefault = React.useMemo(() => {
        if (activePrompt === 'layout') {
            const defaultLayout = {
                slug: 'none',
                order: imageLayout?.order ?? 1,
                gap_size: 10,
                canvas_color: {
                    red: 255,
                    green: 255,
                    blue: 255,
                    gradient: 'none',
                    transparency: 100,
                },
            };

            return !isLayoutChanged(imageLayout, defaultLayout);
        }
        if (!selectedImage) return true;
        if (activePrompt === 'effects') {
            const defaultEffects = { name: 'none', intensity: 100 };
            return !isVisualEffectsChanged(selectedImage.image?.visual_effects, defaultEffects);
        }
        if (activePrompt === 'adjust_visuals') {
            const defaultVisualSettings = {
                brightness: 0,
                contrast: 0,
                saturation: 0,
                shadows: 0,
                vignette: 0,
                highlights: 0,
                vibrance: 0,
                clarity: 0,
                hue: 0,
                blacks: 0,
                white: 0,
                grain: 0,
                red: 1,
                green: 1,
                blue: 1,
            };
            const defaultCanvasColor = {
                red: 255,
                green: 255,
                blue: 255,
                gradient: 'none',
                transparency: 1,
            };
            return (
                !isVisualSettingsChanged(
                    selectedImage.image?.visual_settings,
                    defaultVisualSettings,
                ) && !isCanvasColorChanged(selectedImage.image?.canvas_color, defaultCanvasColor)
            );
        }
        if (activePrompt === 'resize') {
            const defaultTransformation = {
                width: 820,
                height: 615,
                rotation: 0,
                flip: 'none',
                presets: { size: 'none' },
                crop: { x: 0, y: 0, width: 0, height: 0, zoom: 1 },
            };
            return !isTransformationChanged(
                selectedImage.image?.transformation,
                defaultTransformation,
            );
        }

        return true;
    }, [selectedImage, activePrompt, imageLayout]);

    return (
        <div className={s.container_buttons}>
            {activePrompt != null &&
                activePrompt !== 'image_prompt' &&
                activePrompt !== 'image_uploads' &&
                activePrompt !== 'add_text' &&
                activePrompt !== 'enhance' &&
                activePrompt !== 'merger' &&
                activePrompt !== 'transparency' &&
                activePrompt !== 'color' &&
                activePrompt !== 'remove_background' &&
                activePrompt !== 'fullScreen' && (
                    <div
                        onClick={!loadingCancel ? handleCancelChanges : undefined}
                        className={s.container}
                    >
                        {loadingCancel ? (
                            <Spinner color="red" />
                        ) : (
                            <>
                                <IconCancel />
                            </>
                        )}
                        <p>Cancel</p>
                    </div>
                )}
        </div>
    );
};

export default SaveChangesButton;
